import { z } from 'zod'
import type { Product } from '@/features/consignment-form/products'
import {
  goodsDescription,
  MAX_DESCRIPTION,
  MAX_PACKAGES,
  MAX_SIDE_CM,
  MAX_TEXT,
  MAX_WEIGHT_KG,
} from '@/features/consignment-form/rules'
import { t } from '@/lang'

/**
 * What the browser and the route handlers agree on: the shapes that cross between them, and the
 * parts of the f-edge contract both sides would otherwise repeat. Pure Zod, so a route handler
 * and a client component import the same thing.
 */

/** `Idempotency-Key` pattern from the OpenAPI description of POST consignments. */
export const IDEMPOTENCY_KEY = /^[A-Za-z0-9_-]{32,64}$/

/** Port codes from `GET /api/v1/catalog/ports`: Guernsey and Sark. */
export const portCodes = ['GCI', 'SRX'] as const
export type PortCode = (typeof portCodes)[number]
export const ports: Record<PortCode, string> = { GCI: 'Guernsey', SRX: 'Sark' }

export const boundedText = (max = MAX_TEXT) => z.string().trim().min(1).max(max)

const sideInCm = z.number().int().positive().max(MAX_SIDE_CM).optional()

/** One item as both POST routes forward it, in the field names f-edge confirmed. */
export const itemDtoSchema = z
  .strictObject({
    quantity: z.number().int().min(1).max(MAX_PACKAGES),
    tariffCode: boundedText().regex(/^[^<>\p{Cc}]+$/u),
    description: goodsDescription(t('validation.description-required'), MAX_DESCRIPTION),
    weightInKg: z.number().positive().max(MAX_WEIGHT_KG).optional(),
    lengthInCm: sideInCm,
    widthInCm: sideInCm,
    heightInCm: sideInCm,
  })
  // One side on its own cannot give a volume.
  .refine((item) => {
    const given = [item.lengthInCm, item.widthInCm, item.heightInCm].filter((side) => side !== undefined).length
    return given === 0 || given === 3
  })

export const estimateSchema = z.object({
  description: z.string().optional(),
  estimatedAmount: z.number().nonnegative(),
  formattedAmount: z.string(),
  cubicInM3: z.number().nonnegative().optional(),
  pricingMethod: z.enum(['SET', 'WEIGHT', 'CUBIC', 'MINIMUM']),
  quantity: z.number().int().positive().optional(),
  tariffCode: z.string().optional(),
})

export type Estimate = z.infer<typeof estimateSchema>

export const estimateErrorSchema = z.object({
  error: z.enum(['estimate_unavailable', 'estimate_rate_limited', 'estimate_access_unavailable']),
  backend: z.lazy(() => backendErrorSchema).optional(),
})

export type EstimateErrorCode = z.infer<typeof estimateErrorSchema>['error']

/** `Retry-After` as a point in time, from seconds or an HTTP-date; nothing when malformed or already past. */
export function retryAfterTime(value: string | null, now = Date.now()) {
  if (!value) return undefined
  const time = /^\d+$/.test(value) ? now + Number(value) * 1000 : Date.parse(value)
  return Number.isFinite(time) && time > now ? time : undefined
}

/** Backend wording shown to the guest: one bounded line of plain text, never markup or a multi-line dump. */
const backendText = (max: number) => boundedText(max).regex(/^[^<>\p{Cc}]+$/u)

/** One field f-edge refused a create for: its path in the create DTO and f-edge's own explanation. */
export const createIssueSchema = z.object({
  field: z.string().regex(/^[A-Za-z]{1,40}(?:\[\d{1,4}\])?(?:\.[A-Za-z]{1,40})?$/),
  message: backendText(300),
})

export type CreateIssue = z.infer<typeof createIssueSchema>

/** The parts of a backend error the guest is shown, each checked on its own by the create route. */
export const backendErrorSchema = z.object({
  status: z.number().int().min(400).max(599),
  code: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{0,79}$/)
    .optional(),
  title: backendText(100).optional(),
  detail: backendText(300).optional(),
  fieldErrors: z.array(createIssueSchema).max(20).optional(),
})

export type BackendError = z.infer<typeof backendErrorSchema>

const MAX_FIELD_ERRORS = 20

const rawBackendErrorSchema = z
  .object({ code: z.unknown(), title: z.unknown(), detail: z.unknown(), fieldErrors: z.array(z.unknown()) })
  .partial()

const checked = <T>(schema: z.ZodType<T>, value: unknown) => {
  const result = schema.safeParse(value)
  return result.success ? result.data : undefined
}

/**
 * The backend error as the guest may see it. Each part is checked on its own and a part that fails is dropped,
 * so one odd field does not hide the rest. With `fieldErrors` the `detail` is f-edge's generic
 * "Validation failure" and says nothing, so it is left out.
 */
export function parseBackendError(status: number, body: unknown): BackendError | undefined {
  const raw = rawBackendErrorSchema.safeParse(body)
  if (!raw.success) return undefined
  const fieldErrors = (raw.data.fieldErrors ?? [])
    .flatMap((candidate) => checked(createIssueSchema, candidate) ?? [])
    .slice(0, MAX_FIELD_ERRORS)
  const { shape } = backendErrorSchema
  const code = checked(shape.code, raw.data.code)
  const title = checked(shape.title, raw.data.title)
  const detail = fieldErrors.length > 0 ? undefined : checked(shape.detail, raw.data.detail)
  if (!code && !title && !detail && fieldErrors.length === 0) return undefined
  return { status, code, title, detail, ...(fieldErrors.length > 0 ? { fieldErrors } : {}) }
}

/** The catalogue the page was rendered with, or why it is missing: f-edge's error when it sent one the guest may see. */
export type Catalogue = { products: Product[] } | { products: null; error?: BackendError }

/** The create route's answer to a definitive rejection, with the backend's own error when it sent one. */
export const createRejectionSchema = z.object({
  error: z.literal('create_rejected'),
  backend: backendErrorSchema.optional(),
})

/** What a successful create answers with; the number is the one thing the guest is shown. */
export const confirmationSchema = z.object({
  consignmentNumber: boundedText(),
  totalAmount: z.number().nonnegative().optional(),
})
